import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import BoxManager from '../components/boxes/BoxManager';
import CreateGraphButton from '../components/CreateGraphButton';
import DashboardGraphs from '../components/DashboardGraphs';
import DashboardBudgets from '../components/DashboardBudgets';
import UserCicleButton from '../components/UserCircleButton';
import '../css/DashboardPage.css';
import { checkIntegrationConnection, getBillingGraphCount, getBillingPlan } from '../api/RequestUtils';
import { useGlobalStateValue } from '../context/GlobalStateProvider';
import { actionTypes, BOX_TYPES } from '../context/globalReducer';
import GetPlusButton from '../components/GetPlusButton';
import RegisterValueButtons from '../components/RegisterValueButtons';

function DashboardPage() {

  // Context
  const [{ userJWTCookie, templateConnectedToIntegrationData, billingGraphCountData, billingPlan }, dispatch] = useGlobalStateValue();

  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandledRef = useRef(false);

  const [integrationConnectionLoading, setIntegrationConnectionLoading] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('expenses');

  useEffect(() => {
    fetchIntegrationConnection();
    fetchBillingGraphCount();
    fetchBillingPlan();
  }, []);

  // Deep-link: open RegisterValueBox directly when ?register-expense or ?register-income is present
  useEffect(() => {
    if (deepLinkHandledRef.current) return;
    if (integrationConnectionLoading) return;
    if (!templateConnectedToIntegrationData?.hasTemplateConnectedToIntegration) return;

    let valueType = null;
    if (searchParams.has('register-expense')) {
      valueType = 'expense';
    } else if (searchParams.has('register-income')) {
      valueType = 'income';
    }
    if (!valueType) return;

    deepLinkHandledRef.current = true;

    dispatch({
      type: actionTypes.SET_ACTIVE_BOX,
      value: { type: BOX_TYPES.REGISTER_VALUE, data: { valueType } }
    });

    // Strip the param from the URL so a refresh won't reopen the box
    const next = new URLSearchParams(searchParams);
    next.delete('register-expense');
    next.delete('register-income');
    setSearchParams(next, { replace: true });
  }, [integrationConnectionLoading, templateConnectedToIntegrationData, searchParams]);

  // Handle scroll to shrink header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // TODO JOAQUIN: show a warning if your billing resources are getting full

  const fetchIntegrationConnection = async () => {
    try {
      setIntegrationConnectionLoading(true);
      const apiResponse = await checkIntegrationConnection(userJWTCookie);
      if (apiResponse) {
        dispatch({
          type: actionTypes.SET_TEMPLATE_CONNECTED_TO_INTEGRATION_DATA,
          value: apiResponse
        })
      }
    } catch (error) {

    } finally {
      setIntegrationConnectionLoading(false);
    }
  }

  const fetchBillingGraphCount = async () => {
    try {
      const apiResponse = await getBillingGraphCount(userJWTCookie);
      if (apiResponse) {
        dispatch({
          type: actionTypes.SET_BILLING_GRAPH_COUNT_DATA,
          value: apiResponse
        })
      }
    } catch (error) {

    } finally {

    }
  }

  const fetchBillingPlan = async () => {
    try {
      const apiResponse = await getBillingPlan(userJWTCookie);
      if (apiResponse) {
        dispatch({
          type: actionTypes.SET_BILLING_PLAN,
          value: apiResponse.plan
        })
      }
    } catch (error) {

    } finally {

    }
  }

  const showNotionConnectionBox = () => {
    dispatch({
      type: actionTypes.SET_ACTIVE_BOX,
      value: {
        type: BOX_TYPES.PROFILE,
        data: { panel: 'walletconnection' }
      }
    })
  }

  const renderNotConnectedToIntegrationWarning = () => {
    return (
      <button className='dashboard__integrationwarningcontainer' onClick={showNotionConnectionBox}>
        <p>
          <span
            style={{
              fontSize: '1.2em',
              marginRight: '0.5em',
              verticalAlign: 'middle'
            }}
          >
            ⚠️
          </span>
          No integration connected to your account. Newly connected integrations might take 1-2 minutes to reflect in your account.
        </p>
      </button>
    );
  }

  return (
    <div className="dashboard__page">
      <BoxManager />
      <div className={`dashboard__header ${isScrolled ? 'scrolled' : ''}`}>
        <CreateGraphButton />
        <GetPlusButton billingPlan={billingPlan} />
        <UserCicleButton />
      </div>
      <div className='dashboard__body'>
        <div className='dashboard__tabs'>
          <button
            className={`dashboard__tab ${activeTab === 'expenses' ? 'selected' : ''}`}
            onClick={() => setActiveTab('expenses')}
          >
            Expenses
          </button>
          <button
            className={`dashboard__tab ${activeTab === 'budgets' ? 'selected' : ''}`}
            onClick={() => setActiveTab('budgets')}
          >
            Budgets
          </button>
        </div>
        {activeTab === 'expenses' && (
          <>
            {!integrationConnectionLoading && !templateConnectedToIntegrationData?.hasTemplateConnectedToIntegration && renderNotConnectedToIntegrationWarning()}
            {!integrationConnectionLoading && templateConnectedToIntegrationData.hasTemplateConnectedToIntegration && <RegisterValueButtons />}
            <DashboardGraphs />
          </>
        )}
        {activeTab === 'budgets' && <DashboardBudgets />}
      </div>
    </div>
  );
}

export default DashboardPage;